import { Region } from '@heidi/schema/ml-scribe';

const VALID_REGIONS = new Set<string>(Object.values(Region));

// Stands in for a value that is not a `Region` in any casing wherever a log
// must not carry the raw, caller-supplied value itself.
export const NON_CANONICAL_REGION = 'NON_CANONICAL';

/**
 * Match a raw region value against the `Region` enum, normalising case and
 * surrounding whitespace first. Returns `undefined` when the value is not a
 * region in any casing.
 *
 * **Why this is shared rather than inlined.** Normalising is not cosmetic: the
 * resolution chain these readers feed ends at `defaultRegion = AU`, and the
 * resolved region picks the API host on every request (`getDomains()` → the
 * axios `baseURL` interceptor). Compared literally, a `"uk"` claim is not "not
 * found" — it is discarded, the chain falls through, and a UK user's clinical
 * data is addressed to the Australian backend. Silent, and only non-AU regions
 * can exhibit it.
 *
 * That makes DIVERGENCE the real hazard, not strictness on its own: when two
 * readers of the same stored claim normalise differently, one accepts a value
 * the other drops to AU. Keeping one definition is what stops the read path and
 * the write path disagreeing about a single token claim.
 *
 * Trimming matches the Rust offline resolver
 * (`offline_recording::region::region_slug`), which does `.trim()` +
 * `.to_ascii_uppercase()`, so the frontend and the offline drain cannot
 * disagree about the same stored value either.
 *
 * Deliberately dependency-free — no store, no router, no query client — so the
 * hooks that write region state can use it without pulling a module graph in.
 */
export const toValidRegion = (value?: string | null): Region | undefined => {
  if (!value) return undefined;
  const normalized = value.trim().toUpperCase();
  return VALID_REGIONS.has(normalized) ? (normalized as Region) : undefined;
};
